import {
  Flame,
  Leaf,
  Lightbulb,
  Wind,
  Package,
  Sparkles,
  Loader2,
  AlertCircle,

  Grid2X2,
} from "lucide-react";

import {
  useEffect,
  useMemo,
  useState,
  useCallback,
} from "react";

import {
  useNavigate,
  useSearchParams,
} from "react-router-dom";

import ProductCard from "./ProductCard";
import API from "../../services/api";

import "../../styles/categories.css";

/* ==========================================
   MEDIA URL
========================================== */

const MEDIA_URL =
  process.env.REACT_APP_MEDIA_URL ||
  window.location.origin;

/* ==========================================
   ICONOS
========================================== */

const iconMap = {
  Anarguilas: <Flame size={18} />,
  Papers: <Leaf size={18} />,
  Iluminación: <Lightbulb size={18} />,
  Ventilación: <Wind size={18} />,
  Accesorios: <Package size={18} />,
};

/* ==========================================
   HELPERS
========================================== */

const normalizeImage = (image) => {
  if (!image) return null;

  if (
    image.startsWith("http://") ||
    image.startsWith("https://")
  ) {
    return image;
  }

  return `${MEDIA_URL}${image}`;
};

const normalizeProductsResponse = (response) => {
  return (
    response?.data?.results ||
    response?.data?.products ||
    response?.data?.data?.products ||
    response?.data?.data ||
    response?.data ||
    []
  );
};

export default function CategoryFilter() {
  const navigate = useNavigate();

  const [searchParams] =
    useSearchParams();

  const currentCategory =
    searchParams.get("categoria") ||
    "Todos";

  const [products, setProducts] =
    useState([]);

  const [categories, setCategories] =
    useState(["Todos"]);

  const [loading, setLoading] =
    useState(true);

  const [error, setError] =
    useState("");

  /* ==========================================
     LOAD PRODUCTS
  ========================================== */

  const loadProducts =
    useCallback(async () => {
      try {
        setLoading(true);
        setError("");

        const response =
          await API.get("/products/");

        const raw =
          normalizeProductsResponse(
            response
          );

        const productsArray =
          Array.isArray(raw)
            ? raw
            : [];

        const seen = new Map();

        const formatted = productsArray
          .filter((product) => product?.is_active !== false)
          .map((product) => {
            if (!product?.id) return null;

            // 🔥 ELIMINA DUPLICADOS POR ID
            if (seen.has(product.id)) return null;
            seen.set(product.id, true);

            return {
              ...product,
              image:
                normalizeImage(product.image) ||
                normalizeImage(product.image_url),

              price: Number(product.price ?? 0),
              stock: Number(product.stock ?? 0),
              category: product.category || "General",
            };
          })
          .filter(Boolean); // 🔥 elimina nulls   

        setProducts(formatted);

        const uniqueCategories =
          [
            "Todos",
            ...new Set(
              formatted.map(
                (product) =>
                  product.category
              )
            ),
          ];

        setCategories(
          uniqueCategories
        );
      } catch (err) {
        console.error(
          "PRODUCT LOAD ERROR:",
          err
        );

        setError(
          err?.response?.data
            ?.message ||
            err?.response?.data
              ?.detail ||
            "No se pudieron cargar los productos"
        );
      } finally {
        setLoading(false);
      }
    }, []);

  useEffect(() => {
    loadProducts();
  }, [loadProducts]);

  /* ==========================================
     FILTER
  ========================================== */

  const filteredProducts =
    useMemo(() => {
      if (
        currentCategory ===
        "Todos"
      ) {
        return products;
      }

      return products.filter(
        (product) =>
          product.category ===
          currentCategory
      );
    }, [
      products,
      currentCategory,
    ]);

  /* ==========================================
     CATEGORY CLICK
  ========================================== */

  const handleCategoryClick =
    (category) => {
      navigate(
        category === "Todos"
          ? "/catalog"
          : `/catalog?categoria=${encodeURIComponent(
              category
            )}`
      );
    };

  /* ==========================================
     CATEGORY ITEMS
  ========================================== */

  const categoryItems =
    useMemo(
      () =>
        categories.map(
          (category) => ({
            name: category,

            icon:
              category ===
              "Todos"
                ? (
                    <Sparkles
                      size={18}
                    />
                  )
                : iconMap[
                    category
                  ] || (
                    <Grid2X2
                      size={18}
                    />
                  ),
          })
        ),
      [categories]
    );

  return (
    <section className="categories-section">

      <div className="categories-header">
        <span className="categories-badge">
          <Sparkles size={14} />
          EXPLORAR
        </span>

        <h2>
          Categorías Premium
        </h2>

        <p>
      
        </p>
      </div>

      
      <div className="categories">

        {categoryItems.map(
          (category) => (
            <button
              key={
                category.name
              }
              onClick={() =>
                handleCategoryClick(
                  category.name
                )
              }
              className={
                currentCategory ===
                category.name
                  ? "category-btn active"
                  : "category-btn"
              }
            >
              <span>
                {
                  category.icon
                }
              </span>

              <span>
                {
                  category.name
                }
              </span>
            </button>
          )
        )}

      </div>

      <div className="products-grid">

        {loading ? (
          <div className="products-loading">
            <Loader2
              size={32}
              className="spin"
            />
            <span>
              Cargando productos...
            </span>
          </div>
        ) : error ? (
          <div className="products-error">
            <AlertCircle
              size={28}
            />
            <p>{error}</p>

            <button
              onClick={
                loadProducts
              }
            >
              Reintentar
            </button>
          </div>
        ) : filteredProducts.length ===
          0 ? (
          <div className="products-empty">
            <Package
              size={40}
            />
            <p>
              No hay productos
              disponibles
            </p>
          </div>
        ) : (
          filteredProducts.map(
            (product) => (
              <ProductCard
                key={
                  product.id
                }
                product={
                  product
                }
              />
            )
          )
        )}

      </div>

    </section>
  );
}